"use client"

import AnnouncementsList from "@/components/AnnouncementsList"
import HeaderMenu from "@/components/HeaderMenu"
import Logo from "@/components/Logo"
import NavTrigger from "@/components/navTrigger"
import PageLoadingIndicator from "@/components/PageLoadingIndicator"
import SearchTrigger from "@/components/SearchTrigger"
import { useAppContext } from "@/context/app"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { HeaderRightTrigger } from "./header-right-trigger"
import { SearchBarWrapper } from "./search-bar-wrapper"
import classes from "./header.module.css"
import clsx from "clsx"
import debounce from "lodash.debounce"

const Header = ({ announcementsData }: { announcementsData: any }) => {
  const { isMobile } = useIsMobile()
  const pathname = usePathname()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [
    { headerVisibleHeight },
    {
      actions: { setHeaderVisibleHeight },
    },
  ] = useAppContext()
  const headerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const headerHeightRef = useRef(0)
  const menuHeightRef = useRef(0)
  const mobileMenuHeightRef = useRef(0)
  const prevShowHeaderRef = useRef(false)
  const [showHeader, setShowHeader] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    const handler = () => {
      const nextShowHeader = window.pageYOffset > 100

      if (prevShowHeaderRef.current !== nextShowHeader) {
        prevShowHeaderRef.current = nextShowHeader
        setShowHeader(nextShowHeader)
        setHeaderVisibleHeight(
          nextShowHeader
            ? window.matchMedia("(max-width: 959px)").matches
              ? mobileMenuHeightRef.current
              : menuHeightRef.current
            : headerHeightRef.current
        )
      }
    }

    handler()

    window.addEventListener("scroll", handler)

    return () => {
      window.removeEventListener("scroll", handler)
    }
  }, [setHeaderVisibleHeight])

  useEffect(() => {
    const calculateHeaderVisibleHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0
      const menuHeight = menuRef.current?.offsetHeight || 0
      const mobileMenuHeight = mobileMenuRef.current?.offsetHeight || 0
      headerHeightRef.current = headerHeight
      menuHeightRef.current = menuHeight
      mobileMenuHeightRef.current = mobileMenuHeight

      setHeaderVisibleHeight(
        window.scrollY > 100
          ? window.matchMedia("(max-width: 959px)").matches
            ? mobileMenuHeight
            : menuHeight
          : headerHeight
      )
      setHeaderHeight(headerHeight)
    }

    calculateHeaderVisibleHeight()
    const handler = debounce(calculateHeaderVisibleHeight, 600)
    window.addEventListener("resize", handler)

    return () => {
      window.removeEventListener("resize", handler)
    }
  }, [setHeaderVisibleHeight])

  if (pathname === "/checkout") {
    return null
  }

  return (
    <div
      className={cn(
        "duration-300 sticky top-0 z-50 w-full bg-white transition-transform ease-in-out"
      )}
      style={{
        transform: showHeader
          ? `translateY(-${headerHeight - headerVisibleHeight}px)`
          : undefined,
      }}
      ref={headerRef}
    >
      <header
        className={clsx(
          "shadow-sm z-header grid h-auto w-full auto-cols-fr justify-center bg-header ",
          'relative after_pointer-events-none after_absolute after_left-0 after_top-full after_z-[2] after_h-[1px] after_w-full after_bg-[#efefef] after_content-[""]'
        )}
        data-cy="Header-root"
      >
        <div className="grid grid-cols-5 bg-primary">
          <div className="col-span-3 col-start-2">
            <AnnouncementsList data={announcementsData} />
          </div>
          <Link
            href="/contact-us"
            className="col-span-1 col-start-5 hidden cursor-pointer items-center justify-center font-sans text-sm text-white lg_flex"
          >
            <span className={classes.contact} />
            Contact us
          </Link>
        </div>
        {
          // logo and header buttons
        }
        <div
          className="border-0 relative z-header mx-auto grid h-[60px] w-[1720px] max-w-full grid-cols-4 lg_h-[85px]"
          ref={mobileMenuRef}
        >
          {isMobile && (
            <div className="col-start-1 row-start-1 ml-3 grid grid-flow-col gap-4 self-center justify-self-start">
              <NavTrigger />
              <SearchTrigger
                isOpen={isSearchOpen}
                onClick={() => {
                  setIsSearchOpen(true)
                }}
                showHeader={showHeader}
              />
            </div>
          )}

          <Link
            href="/"
            className="col-start-2 col-end-4 mx-auto flex h-full items-center justify-center lg_w-[216px] lg_justify-self-center"
            data-cy="Header-logoContainer"
          >
            <Logo width={isMobile ? 144 : 250} height={isMobile ? 24 : 54} />
          </Link>
          <HeaderRightTrigger
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            className={cn(
              "duration-300 transition-opacity ease-in-out",
              showHeader && !isMobile ? "hidden opacity-0" : ""
            )}
            showHeader={showHeader}
          />
        </div>
        {
          // header menu
        }
        <div
          className="mx-auto hidden w-[1720px] max-w-full grid-cols-5 lg_grid"
          ref={menuRef}
        >
          <div className="flex items-center">
            <Link href="/">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 250 56"
                className={clsx(
                  "h-auto w-[140px]",
                  !showHeader && "hidden",
                  "duration-3xl animate-in  fade-in-0 "
                )}
              >
                <path
                  fill="#2F6132"
                  d="M101.69 18.425h-4.166l-9.25 9.122v-6.534c0-.708-.256-1.323-.766-1.822a2.5 2.5 0 0 0-1.822-.766h-.349v22.11h2.937v-9.993l8.124 8.356c.499.522 1.09.928 1.752 1.218a5.4 5.4 0 0 0 2.078.43h1.915l-11.34-11.501zM107.458 18.425h-2.936v22.11h2.936zM129.544 19.168c-.511-.487-1.114-.743-1.822-.743h-.349v17.13l-10.48-14.867a5.36 5.36 0 0 0-1.915-1.648 5.26 5.26 0 0 0-2.472-.603h-.348v19.556c0 .708.256 1.311.743 1.81.499.5 1.103.743 1.81.743h.349V23.044l10.816 15.39a4.9 4.9 0 0 0 1.776 1.555 5 5 0 0 0 2.309.557h.349V20.979c0-.708-.267-1.323-.766-1.81M145.56 29.254c0 .661.244 1.241.719 1.717.476.476 1.057.72 1.718.72h3.83l-.012.325c-.092 1.207-.487 2.275-1.149 3.157-.673.893-1.555 1.578-2.611 2.054a8.3 8.3 0 0 1-3.424.72c-1.624 0-3.052-.384-4.27-1.138a7.63 7.63 0 0 1-2.809-3.075c-.662-1.289-.987-2.74-.987-4.318 0-1.485.349-2.89 1.033-4.166a8.23 8.23 0 0 1 2.867-3.087c1.219-.767 2.6-1.161 4.12-1.161 1.497 0 2.785.302 3.807.905 1.033.604 1.857 1.358 2.46 2.252a2.83 2.83 0 0 0 1.068.963c.696.36 1.451.453 2.344.163l.337-.117-.116-.336c-.65-1.892-1.822-3.482-3.47-4.735-1.66-1.242-3.784-1.88-6.325-1.88-2.078 0-3.981.51-5.676 1.508a10.86 10.86 0 0 0-4.004 4.097c-.963 1.718-1.45 3.656-1.45 5.745s.464 4.004 1.369 5.698a10.2 10.2 0 0 0 3.888 4.028c1.66.974 3.609 1.474 5.78 1.474 1.88 0 3.586-.372 5.06-1.115 1.474-.742 2.681-1.775 3.563-3.087.882-1.3 1.439-2.797 1.648-4.445.058-.487.093-1.172.093-2.03v-1.184h-9.366v.348zM171.847 32.039c-.302-.766-.812-1.486-1.497-2.136a6.8 6.8 0 0 0-1.961-1.265 13 13 0 0 0-2.38-.696l-2.727-.522c-.963-.186-1.683-.557-2.147-1.091a2.8 2.8 0 0 1-.696-1.904c0-.916.313-1.67.974-2.32a3.77 3.77 0 0 1 2.356-1.126c.395-.035.674-.058.859-.058 1.753 0 3.087.661 4.062 2.019.256.371.592.661.987.87.394.198.824.302 1.288.302.162 0 .383-.023.65-.07l.499-.104-.07-.348c-.174-.882-.545-1.741-1.114-2.565-.568-.836-1.404-1.52-2.46-2.054s-2.356-.801-3.853-.801a8 8 0 0 0-3.494.79c-1.091.521-1.973 1.264-2.634 2.216-.662.952-.998 2.077-.998 3.32 0 1.624.545 2.959 1.613 3.957.534.51 1.149.928 1.822 1.242a9 9 0 0 0 1.961.65l2.855.58c.94.197 1.788.58 2.519 1.149.696.534 1.033 1.288 1.033 2.31 0 1.137-.418 1.984-1.277 2.587-.882.627-1.961.952-3.215.952a4.93 4.93 0 0 1-2.553-.708 5.16 5.16 0 0 1-1.892-1.88 2.93 2.93 0 0 0-1.044-1.056c-.673-.407-1.474-.5-2.147-.29l-.279.08.023.291c.105 1.08.523 2.124 1.242 3.099.708.963 1.66 1.764 2.82 2.367a8.2 8.2 0 0 0 3.819.917c1.392 0 2.669-.267 3.795-.8 1.137-.535 2.042-1.312 2.704-2.31s.986-2.182.986-3.517c.024-.615-.127-1.311-.429-2.077M188.35 40.569h3.238l7.962-16.074 3.876 7.834h-3.667c-.813.023-1.474.244-1.962.661a4.2 4.2 0 0 0-1.068 1.323l-.336.766h8.437l2.728 5.478h3.238l-11.2-22.55zM226.615 29.474c1.172-1.126 1.764-2.634 1.764-4.48 0-1.195-.29-2.31-.859-3.296-.569-.998-1.462-1.799-2.646-2.39-1.172-.581-2.635-.883-4.352-.883h-7.765v22.11h2.937V21.233h4.677c1.799 0 3.098.371 3.876 1.102s1.161 1.637 1.161 2.751-.407 2.043-1.254 2.843c-.835.79-2.124 1.196-3.818 1.196h-.337c-.359 0-.673.127-.917.383a1.28 1.28 0 0 0-.359.882c0 .267.081.51.255.731l5.176 7.068a5.9 5.9 0 0 0 2.02 1.73c.8.405 1.682.614 2.611.614h.696l-6.685-9.157c1.509-.278 2.797-.916 3.819-1.903M248.514 23.822c-.986-1.671-2.379-3.006-4.143-3.958-1.764-.951-3.783-1.439-6.023-1.439h-6.72v22.11h6.72c2.135 0 4.108-.476 5.861-1.405a10.93 10.93 0 0 0 4.224-3.934c1.045-1.683 1.567-3.621 1.567-5.757 0-2.066-.499-3.946-1.486-5.617m-13.95-2.588h3.656c1.764 0 3.331.36 4.642 1.08a7.64 7.64 0 0 1 3.029 2.947c.708 1.254 1.068 2.67 1.068 4.236 0 1.579-.36 3.018-1.091 4.248-.719 1.23-1.752 2.217-3.064 2.913-1.323.708-2.855 1.056-4.572 1.056h-3.691v-16.48zM184.625 30.46l-1.404-2.112-.012.012-2.333-3.528-2.82-4.236a5.2 5.2 0 0 0-1.752-1.52 5.36 5.36 0 0 0-2.554-.65h-.661L181.7 31.26v9.285h.349c.707 0 1.323-.255 1.833-.755.523-.51.778-1.125.778-1.856v-6.256z"
                ></path>
                <path
                  fill="#A1C323"
                  d="M193.701 15.269s-4.77 2.31-7.3 5.93c-1.614 2.31-2.031 5.397-1.544 7.277.72-.836 2.17-2.577 3.609-4.654 1.463-2.112 2.472-3.621 3.204-4.851.336-.569.65-1.068.65-1.068s.023.197-.093.604c-.116.394-1.254 2.506-3.447 6.011-1.068 1.706-2.611 3.68-3.47 4.747 2.309-.661 4.305-1.66 5.652-3.087 4.224-4.515 2.739-10.91 2.739-10.91"
                ></path>
                <path
                  fill="#2F6132"
                  d="M69.252 17.404a28.3 28.3 0 0 0-2.507-4.63 27.6 27.6 0 0 0-3.32-4.028 27 27 0 0 0-4.027-3.32 26.3 26.3 0 0 0-4.63-2.506A27 27 0 0 0 44.16.773c-3.68 0-7.242.72-10.596 2.135a28.3 28.3 0 0 0-4.63 2.507 27.6 27.6 0 0 0-4.028 3.32 27 27 0 0 0-3.32 4.027 26.3 26.3 0 0 0-2.506 4.63 27.1 27.1 0 0 0-2.136 10.596c0 3.494.65 6.894 1.939 10.098a83 83 0 0 1-5.42 5.118c-.023.023-.047.035-.07.058a70.536 70.536 0 0 1-5.989 4.62 49 49 0 0 1-2.68 1.74c-1.544.94-2.46 1.392-2.496 1.404-.626.313-2.228.917-2.228.917s.557 1.16 1.95 1.416q.157.035.313.035c.209 0 .395-.024.569-.058a1.1 1.1 0 0 0 .418-.117c.011 0 .023-.011.058-.023.336-.139.615-.313.928-.487.917-.5 2.437-1.393 4.364-2.704v-.012a79 79 0 0 0 5.466-4.132c.163-.139.325-.267.476-.406 3.563.639 6.743.987 9.633 1.033.348.383.72.755 1.09 1.114.407.546.918 1.01 1.521 1.358.348.29.708.58 1.08.847a27 27 0 0 0 4.886 2.913 27 27 0 0 0 5.477 1.857c1.939.43 3.935.65 5.931.65 3.679 0 7.242-.72 10.596-2.135a28.3 28.3 0 0 0 4.63-2.507 27.6 27.6 0 0 0 4.028-3.32 27 27 0 0 0 3.32-4.026 26.3 26.3 0 0 0 2.506-4.631 27.1 27.1 0 0 0 2.136-10.596c0-3.668-.697-7.242-2.124-10.608M20.658 39.722c.105-.105.198-.209.302-.313a1.24 1.24 0 0 0 .383-.395c.58-.615 1.16-1.254 1.73-1.892 7.81-8.797 11.953-13.3 14.762-16.062 2.913-2.855 4.352-3.795 6.22-4.886.813-.476 1.904-.836 3.076-1.033a11.9 11.9 0 0 1 3.737 0c2.275.383 4.236 1.474 5.478 2.333.43.29.824.673 1.172 1.067-.023 0-.046.012-.07.012-.754.116-2.205.476-3.458 1.694-1.694 1.66-2.484 3.97-2.716 7.997-.209 3.679-1.311 6.522-2.193 8.252-1.613 3.18-4.062 5.802-7.277 7.822-1.01.638-2.565 1.543-4.317 2.124a23 23 0 0 1-4.144.905c-1.601.197-3.04.186-4.155-.012a3.4 3.4 0 0 1-1.02-.348c-.221-.186-.43-.371-.65-.569 1.763-.116 3.411-.37 4.908-.754 1.916-.487 3.633-1.195 5.107-2.112 2.449-1.52 4.515-3.435 6.128-5.71 1.3-1.81 2.31-3.853 3.017-6.058 1.207-3.75 1.103-6.697 1.103-6.825a1.19 1.19 0 0 0-.453-.893 1.2 1.2 0 0 0-.963-.256c-.104.012-2.484.43-5.629 1.52-1.845.64-3.574 1.37-5.141 2.194-1.973 1.033-3.69 2.194-5.107 3.459-1.857 1.671-3.47 3.656-4.642 5.745-1.253 2.228-1.961 4.456-2.031 6.464-.012.29.012.58.046.87-2.135-.057-4.48-.278-6.998-.673a63 63 0 0 0 3.795-3.667m5.583 3.575c.058-1.625.661-3.482 1.729-5.374 1.056-1.869 2.483-3.633 4.155-5.13 1.253-1.125 2.797-2.17 4.584-3.11a35 35 0 0 1 4.793-2.043 40 40 0 0 1 3.819-1.114 20.6 20.6 0 0 1-.929 4.526 19.4 19.4 0 0 1-2.68 5.397 18.9 18.9 0 0 1-5.432 5.06c-1.265.79-2.763 1.404-4.434 1.822-1.578.406-3.377.65-5.315.731-.07 0-.128 0-.197.012a2.7 2.7 0 0 1-.093-.777m40.794-5.63a24.9 24.9 0 0 1-5.315 7.893 24.6 24.6 0 0 1-7.892 5.315 24.8 24.8 0 0 1-9.656 1.95 24.8 24.8 0 0 1-10.4-2.275c-.463-.22-.928-.44-1.38-.685.406-.023.835-.07 1.265-.116 1.567-.197 3.203-.545 4.596-1.01 1.996-.66 3.725-1.67 4.84-2.367 3.585-2.251 6.324-5.2 8.146-8.774a22.5 22.5 0 0 0 1.776-4.758c.36-1.451.592-2.948.673-4.457.244-4.155 1.103-5.536 1.985-6.406.79-.766 1.74-.975 2.112-1.033a5 5 0 0 1 1.01-.035c.545.012 1.218.023 1.683-.546.336-.406.394-.812.383-1.09-.012-.29-.116-.72-.534-1.161-.116-.116-.255-.29-.43-.487-.533-.627-1.276-1.498-2.205-2.136-1.45-.998-3.737-2.275-6.453-2.727a14 14 0 0 0-4.514 0c-1.486.243-2.832.696-3.911 1.334-1.962 1.15-3.587 2.206-6.697 5.246-2.855 2.797-7.021 7.335-14.867 16.179-.186.209-.371.417-.557.615A24.6 24.6 0 0 1 19.335 28c0-3.354.662-6.604 1.95-9.656a24.9 24.9 0 0 1 5.315-7.892 24.6 24.6 0 0 1 7.892-5.315 24.8 24.8 0 0 1 9.656-1.95c3.354 0 6.604.661 9.656 1.95a24.9 24.9 0 0 1 7.892 5.315 24.6 24.6 0 0 1 5.316 7.892A24.8 24.8 0 0 1 68.962 28c0 3.343-.639 6.604-1.927 9.668"
                ></path>
                <path
                  fill="#2F6132"
                  d="M49.197 20.967a1.474 1.474 0 1 0 0-2.948 1.474 1.474 0 0 0 0 2.948"
                ></path>
              </svg>
            </Link>
          </div>
          <div className="col-span-3 col-start-2">
            <HeaderMenu />
          </div>

          <div className="col-span-1 col-start-5 flex items-center justify-end">
            <HeaderRightTrigger
              isSearchOpen={isSearchOpen}
              setIsSearchOpen={setIsSearchOpen}
              className={cn(
                "duration-300 transition-opacity ease-in-out",
                showHeader ? "" : "hidden opacity-0"
              )}
              showHeader={showHeader}
            />
          </div>
        </div>
        <SearchBarWrapper
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          className={cn(
            "duration-300 transition-opacity ease-in-out",
            showHeader ? "mt-[50px]" : ""
          )}
          showHeader={showHeader}
        />
        <PageLoadingIndicator absolute />
      </header>
    </div>
  )
}

export default Header
